import React from "react";
import {
  DataTable,
  GROUPED_TABLE_CLASS_NAME,
  GroupHeaderRow,
  getGroupedRowClassName,
  useGroupedRows,
  type Row,
  type SortingState,
} from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { useTableSettings } from "~/shared/hooks/useTableSettings";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { DriverShiftsGroupHeader, TruckShiftsGroupHeader } from "./ShiftsGroupHeader";
import { useShiftColumns, type ShiftColumnHandlers, type ShiftColumnView } from "./ShiftsTable";
import { flattenShiftGroups, type ShiftGroup, type ShiftRow } from "../helpers/shiftGroups";
import { SHIFT_VIEWS, type ShiftsView } from "../model/use-shifts-view";
import { useShowOnDashboard } from "~/shared/hooks/useShowOnDashboard";

interface ShiftsCascadeProps extends ShiftColumnHandlers {
  groups: ShiftGroup[];
  /** Which way `groups` were built — picks the header, the columns and their saved layout. */
  view: ShiftsView;
  trucksById: ShiftColumnView["trucksById"];
  loading?: boolean;
}

/**
 * The autopark shifts screen: one column header at the top, then a collapsible
 * card per truck (or per driver) holding its shifts.
 *
 * It is a single `DataTable` — the group headers are rows rendered through
 * `renderCustomRow` — which is what keeps every group's columns aligned under
 * one shared header.
 */
export const ShiftsCascade: React.FC<ShiftsCascadeProps> = ({
  groups,
  view,
  trucksById,
  loading = false,
  ...handlers
}) => {
  const t = useT();
  const { selectedCompanyId } = useSelectedCompany();
  const { columnConfig, handleColumnConfigChange } = useTableSettings(
    SHIFT_VIEWS[view].settingsTable,
    selectedCompanyId || undefined,
  );

  // The truck card opens from a plate — the group header's in the truck view, the
  // row's in the driver view — and is held here so the whole cascade shares one.
  const { showTruck } = useShowOnDashboard();

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const columns = useShiftColumns(handlers, { view, onPlateClick: showTruck, trucksById });

  const rows = React.useMemo(() => flattenShiftGroups(groups), [groups]);
  const { data, isGroupExpanded, toggleGroup } = useGroupedRows<ShiftRow>({
    rows,
    columns,
    sorting,
  });

  const renderCustomRow = React.useCallback(
    (row: Row<ShiftRow>, _index: number, visibleColumnCount: number) => {
      const shiftRow = row.original;
      if (!shiftRow._isGroupHeader || !shiftRow._group) return undefined;

      const expanded = isGroupExpanded(shiftRow);

      return (
        <GroupHeaderRow
          key={`group-${shiftRow._groupKey}`}
          colSpan={visibleColumnCount}
          isExpanded={expanded}
          onToggle={() => toggleGroup(shiftRow)}
        >
          {shiftRow._group.kind === "driver" ? (
            <DriverShiftsGroupHeader group={shiftRow._group} isExpanded={expanded} />
          ) : (
            <TruckShiftsGroupHeader
              group={shiftRow._group}
              isExpanded={expanded}
              onPlateClick={showTruck}
            />
          )}
        </GroupHeaderRow>
      );
    },
    [isGroupExpanded, toggleGroup, showTruck],
  );

  return (
    <div className="w-full min-w-0 flex flex-col min-h-0 flex-1">
      <DataTable<ShiftRow>
        columns={columns}
        data={data}
        loading={loading}
        rowKey={(row) => (row._isGroupHeader ? `group-${row._groupKey}` : `shift-${row.id}`)}
        renderCustomRow={renderCustomRow}
        // Groups are sorted internally so their headers stay pinned above them.
        manualSorting
        onSortChange={setSorting}
        rowClassName={getGroupedRowClassName}
        className={GROUPED_TABLE_CLASS_NAME}
        columnConfig={columnConfig}
        onColumnConfigChange={handleColumnConfigChange}
        namespace={Namespace.Autopark}
        empty={
          <div className="p-6 text-sm text-muted-foreground text-center">
            {t(Namespace.Autopark, "empty.title")}
          </div>
        }
      />
    </div>
  );
};
